import React, { useMemo } from 'react';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

type Period = 'AM' | 'PM';

export function formatScheduleTime12(time24: string): string {
  if (!time24 || !/^\d{1,2}:\d{2}$/.test(time24)) return time24;
  const parts = time24.split(':');
  const hour = parseInt(parts[0] || '0', 10);
  const minute = parseInt(parts[1] || '0', 10);
  const period: Period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

function parseTime24(value: string): { hour12: number; minute: number; period: Period } {
  const parts = (value || '08:00').split(':');
  const hour24 = parseInt(parts[0] || '8', 10);
  const minute = parseInt(parts[1] || '0', 10);
  const period: Period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return { hour12, minute, period };
}

export function toTime24(hour12: number, minute: number, period: Period): string {
  let hour24 = hour12 % 12;
  if (period === 'PM') hour24 += 12;
  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const TIME_SELECT_CLASS =
  'h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed';

interface ScheduleTimeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  idPrefix?: string;
}

const ScheduleTimeInput: React.FC<ScheduleTimeInputProps> = ({
  value,
  onChange,
  disabled = false,
  idPrefix = 'time',
}) => {
  const { hour12, minute, period } = parseTime24(value);

  const minuteOptions = useMemo(() => {
    const set = new Set(MINUTES);
    if (!set.has(minute)) set.add(minute);
    return Array.from(set).sort((a, b) => a - b);
  }, [minute]);

  const update = (patch: Partial<{ hour12: number; minute: number; period: Period }>) => {
    const next = {
      hour12: patch.hour12 ?? hour12,
      minute: patch.minute ?? minute,
      period: patch.period ?? period,
    };
    onChange(toTime24(next.hour12, next.minute, next.period));
  };

  return (
    <div className="inline-flex items-center gap-1">
      <select
        id={`${idPrefix}-hour`}
        className={`${TIME_SELECT_CLASS} w-[3.25rem]`}
        value={hour12}
        disabled={disabled}
        aria-label="Hour"
        onChange={(e) => update({ hour12: parseInt(e.target.value, 10) })}
      >
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-sm font-medium text-muted-foreground">:</span>
      <select
        id={`${idPrefix}-minute`}
        className={`${TIME_SELECT_CLASS} w-[3.25rem]`}
        value={minute}
        disabled={disabled}
        aria-label="Minute"
        onChange={(e) => update({ minute: parseInt(e.target.value, 10) })}
      >
        {minuteOptions.map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, '0')}
          </option>
        ))}
      </select>
      <select
        id={`${idPrefix}-period`}
        className={`${TIME_SELECT_CLASS} w-[4.25rem]`}
        value={period}
        disabled={disabled}
        aria-label="AM or PM"
        onChange={(e) => update({ period: e.target.value as Period })}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

interface ScheduleTimeRangeProps {
  startTime: string;
  endTime: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  disabled?: boolean;
  idPrefix?: string;
}

export const ScheduleTimeRange: React.FC<ScheduleTimeRangeProps> = ({
  startTime,
  endTime,
  onStartChange,
  onEndChange,
  disabled = false,
  idPrefix = 'range',
}) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground w-10 shrink-0">
        From
      </span>
      <ScheduleTimeInput
        value={startTime}
        onChange={onStartChange}
        disabled={disabled}
        idPrefix={`${idPrefix}-start`}
      />
    </div>
    <div className="hidden sm:flex items-center text-muted-foreground" aria-hidden>
      <span className="text-lg leading-none">→</span>
    </div>
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground w-10 shrink-0">
        To
      </span>
      <ScheduleTimeInput
        value={endTime}
        onChange={onEndChange}
        disabled={disabled}
        idPrefix={`${idPrefix}-end`}
      />
    </div>
  </div>
);

export default ScheduleTimeInput;
